import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PageLayout from '../components/PageLayout';
import Pagination from '../components/Pagination';
import NativeAdCard from '../components/NativeAdCard';
import { useAds, injectAds } from '../hooks/useAds';
import axios from 'axios';
import { Clock, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';

import { API } from '../config';

const LIMIT = 9;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const ads = useAds('blog');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog`, { params: { limit: LIMIT, page } })
      .then(r => {
        if (Array.isArray(r.data)) {
          setPosts(r.data);
          setTotal(r.data.length);
        } else {
          setPosts(r.data.posts || r.data.items || []);
          setTotal(r.data.total || 0);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page]);

  const pages = Math.ceil(total / LIMIT);

  const handlePageChange = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <PageLayout>
      <SEOHead
        title="Travel Blog — Airport Tips & Layover Guides"
        description="Read airport guides, layover tips, seat reviews, and travel stories from the Fly N Out community. Expert advice from frequent flyers around the world."
        url="/blog"
        keywords="airport blog, layover tips, airport guides, travel blog"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Travel Blog</h1>
          <p className="text-muted-foreground">Airport guides, travel tips, and community stories</p>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}
          </div>
        ) : (
          <>
            {/* Grid with ads naturally injected at configured position */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {injectAds(posts, ads).map((item, i) =>
                item.__isAd
                  ? <NativeAdCard key={`ad-${item.ad_id}`} ad={item} variant="blog" />
                  : <PostCard key={item.post_id} post={item} index={i} />
              )}
            </div>
            <Pagination page={page} pages={pages} total={total} limit={LIMIT} onPageChange={handlePageChange} />
          </>
        )}
      </div>
    </PageLayout>
  );
}

export function PostCard({ post, index = 0, compact = false }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="h-full">
      <Link to={`/blog/${post.slug}`} className="block h-full">
        <Card className="fly-card overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer h-full">
          {post.cover_image && (
            <div className={`overflow-hidden ${compact ? 'h-28' : 'h-40'}`}>
              <img
                src={post.cover_image}
                alt={post.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                loading="lazy"
                onError={e => { e.target.parentElement.style.display = 'none'; }}
              />
            </div>
          )}
          <div className="p-4">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {post.tags?.slice(0, 2).map(tag => (
                <Badge key={tag} className="text-[10px] bg-[#1E3A8A]/10 text-[#1E3A8A] border-[#1E3A8A]/20">{tag}</Badge>
              ))}
            </div>
            <h2 className="text-sm font-semibold leading-tight mb-2 line-clamp-2" style={{ fontFamily: 'Space Grotesk' }}>{post.title}</h2>
            {!compact && <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{post.excerpt}</p>}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.read_time} min</span>
              <div className="flex items-center gap-2">
                {(post.like_count || 0) > 0 && (
                  <span className="flex items-center gap-0.5 text-red-400"><Heart className="w-3 h-3" />{post.like_count}</span>
                )}
              </div>
            </div>
          </div>
        </Card>
      </Link>
    </motion.div>
  );
}
